/**
 * Zod-validated environment parsing. The app refuses to boot if anything required is missing —
 * a deliberate choice from CLAUDE.md: a missing secret should stop the process loudly at start,
 * not surface as an undefined at 4pm on deadline day.
 *
 * Every variable here must also appear in .env.example with a comment.
 */
import { z } from "zod";
// Names only — `provider.js` is the one email module with no Nodemailer import, so validating the
// relay order here does not drag a mail transport into the browser bundles that import this file.
import { RELAY_NAMES, type RelayName } from "./email/provider.js";

const nonEmpty = z.string().min(1);

/** Base64-encoded 32-byte key. Validated by decoded length, not string length. */
const base64Key32 = nonEmpty.refine((value) => {
  try {
    return Buffer.from(value, "base64").length === 32;
  } catch {
    return false;
  }
}, "must be 32 bytes encoded as base64");

const baseSchema = z.object({
  NODE_ENV: z.enum(["development", "test", "production"]).default("development"),
  LOG_LEVEL: z.enum(["fatal", "error", "warn", "info", "debug", "trace"]).default("info"),
  DEFAULT_TIMEZONE: nonEmpty.default("Africa/Lagos"),

  API_PORT: z.coerce.number().int().positive().default(4010),

  // Public origins. Same-origin API on each host, so these double as the CORS allow-list.
  APP_BASE_URL: z.string().url(),
  PUBLIC_BASE_URL: z.string().url(),
  PORTAL_BASE_URL: z.string().url(),

  DATABASE_URL: nonEmpty,
  REDIS_URL: nonEmpty,

  SESSION_SECRET: nonEmpty.min(32, "must be at least 32 characters"),
  CSRF_SECRET: nonEmpty.min(32, "must be at least 32 characters"),
  SIGNED_URL_SECRET: nonEmpty.min(32, "must be at least 32 characters"),
  // Losing this makes every encrypted NIN and bank detail permanently unreadable.
  // See docs/06-DEPLOYMENT-AND-OPS.md — store it in two independent places.
  FIELD_ENCRYPTION_KEY: base64Key32,

  SMTP_HOST: nonEmpty,
  SMTP_PORT: z.coerce.number().int().positive().default(587),
  SMTP_SECURE: z
    .enum(["true", "false"])
    .default("false")
    .transform((value) => value === "true"),
  SMTP_USER: z.string().default(""),
  SMTP_PASSWORD: z.string().default(""),
  // `Name <address>` needs quoting in a shell but must NOT arrive quoted: Docker's env-file parser
  // keeps the quotes where dotenv strips them, and nodemailer then reads the whole quoted string as
  // a display name — every email goes out from `Seplat Growth Academy < >`. Strip a wrapping pair
  // here so the header is right whichever loader filled the variable.
  SMTP_FROM: nonEmpty.transform((value) => value.replace(/^"(.*)"$/s, "$1").trim()),
  // Which provider the `email` queue uses. `resend` is stubbed and throws until implemented —
  // see docs/06-DEPLOYMENT-AND-OPS.md on keeping a failover ready for deadline day.
  EMAIL_PROVIDER: z.enum(["smtp", "resend"]).default("smtp"),
  // ── Which relay is tried first ─────────────────────────────────────────────
  //
  // A comma-separated order over `smtp` (whatever SMTP_HOST points at), `smtp-fallback` and
  // `mailgun`. A relay named here but left unconfigured drops out silently; a relay configured but
  // not named is not used at all.
  //
  // Order is a cost and reliability decision, not a preference. A metered free tier is worth
  // spending before a paid one, and the two SMTP relays share a failure mode — both speak port 587
  // — that the HTTPS relay does not. Defaults to the order this shipped with, so a deployment that
  // sets nothing behaves as it did before.
  EMAIL_RELAY_ORDER: z
    .string()
    .default(RELAY_NAMES.join(","))
    .transform((value, ctx): RelayName[] => {
      const names = value
        .split(",")
        .map((name) => name.trim().toLowerCase())
        .filter(Boolean);

      if (names.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "must name at least one relay",
        });
        return z.NEVER;
      }

      const seen = new Set<string>();
      for (const name of names) {
        if (!(RELAY_NAMES as readonly string[]).includes(name)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `unknown relay "${name}" — expected one of ${RELAY_NAMES.join(", ")}`,
          });
          return z.NEVER;
        }
        // A relay listed twice would be tried twice on the same message: two refusals against the
        // same provider, and on a metered one, two counts against the same allowance.
        if (seen.has(name)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `relay "${name}" is listed more than once`,
          });
          return z.NEVER;
        }
        seen.add(name);
      }

      return names as RelayName[];
    }),
  // ── The failover SMTP relay ────────────────────────────────────────────────
  //
  // A second, independent relay tried when the primary cannot deliver. docs/06-DEPLOYMENT-AND-OPS
  // .md: "When Zoho throttles at 4 p.m. on deadline day, you will want it." Deadline day is the
  // whole reason this exists — an OTP that does not arrive is an application that cannot be made.
  //
  // Blank host = no failover, and a single-provider setup behaves exactly as before. It is
  // deliberately not required in production: a misconfigured second relay that silently swallows
  // mail would be worse than none, so switching it on is an explicit act.
  SMTP_FALLBACK_HOST: z.string().default(""),
  SMTP_FALLBACK_PORT: z.coerce.number().int().positive().default(587),
  SMTP_FALLBACK_SECURE: z
    .enum(["true", "false"])
    .default("false")
    .transform((value) => value === "true"),
  SMTP_FALLBACK_USER: z.string().default(""),
  SMTP_FALLBACK_PASSWORD: z.string().default(""),
  // Falls back to SMTP_FROM when blank. A different relay often has to use a different envelope
  // sender to pass its own authentication, so it gets its own knob.
  //
  // Same quote-stripping as SMTP_FROM, and for the same reason: Docker's env-file parser keeps the
  // quotes dotenv strips, and nodemailer then reads the whole quoted string as a display name. Left
  // off, every message the failover sent — on the one day the failover matters — would have gone
  // out from `Seplat Growth Academy < >`.
  SMTP_FALLBACK_FROM: z
    .string()
    .default("")
    .transform((value) => value.replace(/^"(.*)"$/s, "$1").trim()),
  // ── The last relay in the chain: Mailgun over HTTPS ────────────────────────
  //
  // Deliberately a different transport from the two SMTP relays ahead of it. Those both speak port
  // 587, so an outbound-SMTP block or a reputation problem that follows the sending domain can
  // take both out at once; an HTTPS call to a different vendor does not share that failure.
  //
  // Blank key = not in the chain.
  MAILGUN_API_KEY: z.string().default(""),
  MAILGUN_DOMAIN: z.string().default(""),
  // US and EU are separate accounts with separate keys — a key from one returns 404 for a domain
  // in the other, which is the most common way this is misconfigured.
  MAILGUN_BASE_URL: z.string().url().default("https://api.mailgun.net"),
  MAILGUN_FROM: z
    .string()
    .default("")
    .transform((value) => value.replace(/^"(.*)"$/s, "$1").trim()),
  // Messages Mailgun's plan allows per UTC day; 0 means "unmetered, do not count".
  //
  // Only meaningful when Mailgun is ahead of another relay. The chain would otherwise learn the cap
  // the expensive way — one refused HTTPS round-trip per message for the rest of the day, adding
  // latency to exactly the OTPs that must not be slow, and hammering an account that has already
  // said no. Counted in Redis by `DailyQuotaEmailProvider`; when the budget is spent the relay is
  // skipped and the next one in the order sends.
  MAILGUN_DAILY_LIMIT: z.coerce.number().int().nonnegative().default(0),

  TURNSTILE_SITE_KEY: z.string().default(""),
  TURNSTILE_SECRET_KEY: z.string().default(""),

  STORAGE_DRIVER: z.enum(["local", "s3"]).default("local"),
  STORAGE_LOCAL_PATH: nonEmpty.default("/data/uploads"),

  /**
   * Google Sheets export (§9.3). Optional, and deliberately not required in production: a
   * programme that never connects a Google account should not be a reason the API refuses to boot.
   * The connect endpoint returns a clear error when these are blank rather than failing at the
   * OAuth redirect, where the message would be Google's rather than ours.
   */
  GOOGLE_CLIENT_ID: z.string().default(""),
  GOOGLE_CLIENT_SECRET: z.string().default(""),
  GOOGLE_REDIRECT_URI: z.string().default(""),

  SENTRY_DSN: z.string().default(""),

  /**
   * Which sites may embed a published form in an `<iframe>` — the `frame-ancestors` allow-list.
   *
   * Empty by default, and empty means nobody: these pages collect NIN and bank details, so
   * framing is opt-in and enumerated rather than open. Anything not listed here cannot frame a
   * form at all, which is also what closes the clickjacking hole that existed while no
   * `frame-ancestors` header was sent at all.
   *
   * Space-separated, because that is exactly the syntax CSP wants and nginx interpolates this
   * value straight into the header (ops/nginx/spa.conf.template). One variable, no transformation
   * step between the declaration and the thing enforcing it — a comma-separated list would have to
   * be rewritten somewhere, and that somewhere is where the two would drift apart.
   *
   * Scheme + host [+ port] only. A path or a trailing slash is meaningless to `frame-ancestors`
   * and silently matches nothing, so it is rejected here instead of failing quietly in a browser.
   */
  EMBED_ALLOWED_ORIGINS: z
    .string()
    .default("")
    .refine(
      (value) =>
        value
          .split(/\s+/)
          .filter(Boolean)
          .every((origin) => /^https?:\/\/[^/\s]+$/.test(origin)),
      "must be space-separated origins like https://example.com — no paths, no trailing slash",
    ),
});

/**
 * Production tightens what development may leave blank. Turnstile with an empty secret would
 * silently accept every bot; better to refuse to start.
 */
const envSchema = baseSchema.superRefine((env, ctx) => {
  // Checked in every environment, deliberately ahead of the production-only guard below: an order
  // that names only relays nobody configured leaves no way to send anything, and that is as broken
  // in development as in production. Caught at boot rather than at the first OTP — the difference
  // between a failed deploy and applicants who cannot receive a code.
  const configured: Record<RelayName, boolean> = {
    smtp: Boolean(env.SMTP_HOST),
    "smtp-fallback": Boolean(env.SMTP_FALLBACK_HOST),
    mailgun: Boolean(env.MAILGUN_API_KEY),
  };
  // Zod still runs a cross-field refinement when a field's own transform rejected, so the value
  // here can be the raw string rather than the parsed list. The field-level issue already names
  // the problem; adding a second one about it would only bury the first.
  if (
    Array.isArray(env.EMAIL_RELAY_ORDER) &&
    !env.EMAIL_RELAY_ORDER.some((name) => configured[name])
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["EMAIL_RELAY_ORDER"],
      message:
        `names no relay that is configured (${env.EMAIL_RELAY_ORDER.join(", ")}) — ` +
        "set SMTP_HOST, SMTP_FALLBACK_HOST or MAILGUN_API_KEY, or name a relay that is",
    });
  }

  if (env.NODE_ENV !== "production") return;

  const requiredInProduction = [
    "SMTP_USER",
    "SMTP_PASSWORD",
    "TURNSTILE_SITE_KEY",
    "TURNSTILE_SECRET_KEY",
  ] as const;

  for (const key of requiredInProduction) {
    if (!env[key]) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [key],
        message: "is required when NODE_ENV=production",
      });
    }
  }

  // A failover relay named but not authenticated is the worst of both worlds: the primary fails
  // over to it on deadline day and every message is refused at AUTH. Refuse to boot instead.
  if (env.SMTP_FALLBACK_HOST) {
    for (const key of ["SMTP_FALLBACK_USER", "SMTP_FALLBACK_PASSWORD"] as const) {
      if (!env[key]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [key],
          message: "is required when SMTP_FALLBACK_HOST is set",
        });
      }
    }
  }

  // Same reasoning: a key with no domain cannot address a message, and the failure would only be
  // discovered at the moment the chain reached it.
  if (env.MAILGUN_API_KEY && !env.MAILGUN_DOMAIN) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["MAILGUN_DOMAIN"],
      message: "is required when MAILGUN_API_KEY is set",
    });
  }
});

export type Env = z.infer<typeof baseSchema>;

/**
 * Parse and validate. Throws with every problem listed at once — one restart per fix, not ten.
 * Values are never logged: an invalid secret is still a secret.
 */
export const parseEnv = (source: NodeJS.ProcessEnv = process.env): Env => {
  const result = envSchema.safeParse(source);

  if (!result.success) {
    const problems = result.error.issues
      .map((issue) => `  - ${issue.path.join(".") || "(root)"}: ${issue.message}`)
      .sort()
      .join("\n");

    throw new Error(
      `Invalid environment configuration. The application will not start.\n\n${problems}\n\n` +
        `Every variable is documented in .env.example. Copy it to .env and fill in the blanks.`,
    );
  }

  return result.data;
};

let cached: Env | undefined;

/** Memoised accessor, so parsing happens once per process. */
export const getEnv = (): Env => {
  cached ??= parseEnv();
  return cached;
};

/** Test-only escape hatch; production code must not call this. */
export const resetEnvCache = (): void => {
  cached = undefined;
};

/**
 * The embed allow-list as a list, and the `frame-ancestors` value derived from it.
 *
 * `'self'` is always included so the app can frame its own pages; when the allow-list is empty the
 * result is effectively "only me", which is the safe default for a page collecting personal data.
 */
export const embedAllowedOrigins = (source?: string): string[] =>
  (source ?? getEnv().EMBED_ALLOWED_ORIGINS).split(/\s+/).filter(Boolean);

export const frameAncestorsValue = (source?: string): string =>
  ["'self'", ...embedAllowedOrigins(source)].join(" ");
